import {
  CalendarEventPreview,
  CalendarEventTooltip,
  type CalendarPreviewState,
} from '../components/CalendarEventPreview';
import { eventDetailUrl } from '../../shared/event-url';
import { useBranding } from '../branding';
import { rememberCalendarReturnTo } from '../navigation-state';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import type { EventDropArg, EventSourceFunc } from '@fullcalendar/core';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import type { Calendar, CalendarEvent, EventOccurrence } from '../../shared/contracts';
import { api, type ClientSession } from '../api';
import { formatEventRange, searchEvents } from '../format';
import { useDialogFocus } from '../components/useDialogFocus';
import { SubscriptionModal } from '../components/SubscriptionModal';
import { ScrollableCalendarList } from '../components/ScrollableCalendarList';
import { calendarMutationInput, eventInput, seriesMutationInput } from '../calendar-interactions';

const EventEditor = lazy(() =>
  import('../components/EventEditor').then((module) => ({ default: module.EventEditor })),
);

type ViewName = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listMonth';

function viewFromUrl(): ViewName {
  const requested = new URLSearchParams(location.search).get('view') as ViewName | null;
  if (requested && ['dayGridMonth', 'timeGridWeek', 'timeGridDay', 'listMonth'].includes(requested))
    return requested;
  const saved = localStorage.getItem('dragonfly-calendar-view') as ViewName | null;
  return saved || (matchMedia('(max-width: 720px)').matches ? 'listMonth' : 'dayGridMonth');
}

export function CalendarPage({
  calendars,
  session,
}: {
  calendars: Calendar[];
  session: ClientSession | null;
}) {
  const { config } = useBranding();
  const params = useMemo(() => new URLSearchParams(location.search), []);
  const initialIds = (params.get('calendars') || localStorage.getItem('dragonfly-calendar-ids'))
    ?.split(',')
    .filter((id) => calendars.some((calendar) => calendar.id === id));
  const [selected, setSelected] = useState<string[]>(
    initialIds?.length ? initialIds : calendars.map((calendar) => calendar.id),
  );
  const [query, setQuery] = useState(params.get('search') || '');
  const [view, setView] = useState<ViewName>(viewFromUrl);
  const [events, setEvents] = useState<EventOccurrence[]>([]);
  const [calendarPreview, setCalendarPreview] = useState<CalendarPreviewState>();
  const [editorEvent, setEditorEvent] = useState<CalendarEvent | null | undefined>(undefined);
  const [seriesEvent, setSeriesEvent] = useState<CalendarEvent>();
  const [editorRecurrenceId, setEditorRecurrenceId] = useState<string>();
  const [editorStart, setEditorStart] = useState<Date>();
  const [subscribe, setSubscribe] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [online, setOnline] = useState(navigator.onLine);
  const [changeNotice, setChangeNotice] = useState('');
  const [pendingGesture, setPendingGesture] = useState<{
    occurrence: EventOccurrence;
    input: ReturnType<typeof calendarMutationInput>;
    revert: () => void;
  }>();
  const [undo, setUndo] = useState<{
    occurrence: EventOccurrence;
    input: ReturnType<typeof eventInput>;
    version: number;
    scope: 'occurrence' | 'series';
  }>();
  const scopeRef = useDialogFocus(
    () => {
      pendingGesture?.revert();
      setPendingGesture(undefined);
    },
    { enabled: Boolean(pendingGesture) },
  );
  const filterRef = useRef<HTMLButtonElement>(null);
  const previewRef = useRef<CalendarPreviewState | undefined>(undefined);
  const pendingPreview = useRef<string | undefined>(undefined);
  const focusedPreview = useRef<string | undefined>(undefined);
  const previewOpenTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const previewCloseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const previewId = 'calendar-event-tooltip';
  const setPreview = useCallback((next: CalendarPreviewState | undefined) => {
    previewRef.current = next;
    setCalendarPreview(next);
  }, []);
  const dismissPreview = useCallback(() => {
    clearTimeout(previewOpenTimer.current);
    clearTimeout(previewCloseTimer.current);
    pendingPreview.current = undefined;
    focusedPreview.current = undefined;
    setPreview(undefined);
  }, [setPreview]);
  const previewPosition = useCallback((event: EventOccurrence, root: HTMLElement) => {
    const rect = root.getBoundingClientRect();
    return {
      event,
      left: Math.max(12, Math.min(rect.left, window.innerWidth - 332)),
      top: rect.bottom + 280 < window.innerHeight ? rect.bottom + 8 : Math.max(12, rect.top - 272),
    };
  }, []);
  const previewOnHover = useCallback(
    (event: EventOccurrence, root: HTMLElement) => {
      clearTimeout(previewOpenTimer.current);
      pendingPreview.current = event.occurrenceId;
      if (previewRef.current?.event.occurrenceId === event.occurrenceId) {
        clearTimeout(previewCloseTimer.current);
        return;
      }
      previewOpenTimer.current = setTimeout(() => {
        clearTimeout(previewCloseTimer.current);
        pendingPreview.current = undefined;
        setPreview(previewPosition(event, root));
      }, 450);
    },
    [previewPosition, setPreview],
  );
  const previewOnFocus = useCallback(
    (event: EventOccurrence, root: HTMLElement) => {
      clearTimeout(previewOpenTimer.current);
      clearTimeout(previewCloseTimer.current);
      pendingPreview.current = undefined;
      focusedPreview.current = event.occurrenceId;
      setPreview(previewPosition(event, root));
    },
    [previewPosition, setPreview],
  );
  const previewOnLeave = useCallback(
    (occurrenceId: string) => {
      if (pendingPreview.current === occurrenceId) {
        clearTimeout(previewOpenTimer.current);
        pendingPreview.current = undefined;
      }
      if (focusedPreview.current === occurrenceId) return;
      if (previewRef.current?.event.occurrenceId !== occurrenceId) return;
      clearTimeout(previewCloseTimer.current);
      previewCloseTimer.current = setTimeout(() => setPreview(undefined), 150);
    },
    [setPreview],
  );
  const previewOnBlur = useCallback(
    (occurrenceId: string) => {
      if (focusedPreview.current === occurrenceId) focusedPreview.current = undefined;
      previewOnLeave(occurrenceId);
    },
    [previewOnLeave],
  );
  const keepPreviewOpen = useCallback(() => clearTimeout(previewCloseTimer.current), []);
  const closePreviewSoon = useCallback(() => {
    clearTimeout(previewCloseTimer.current);
    previewCloseTimer.current = setTimeout(() => setPreview(undefined), 150);
  }, [setPreview]);
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismissPreview();
    };
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', dismissPreview);
    return () => {
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', dismissPreview);
      clearTimeout(previewOpenTimer.current);
      clearTimeout(previewCloseTimer.current);
    };
  }, [dismissPreview]);
  const closeFilters = () => {
    setFiltersOpen(false);
    filterRef.current?.focus();
  };
  const calendarRef = useRef<FullCalendar>(null);
  const staff = Boolean(session?.user && calendars.some((calendar) => calendar.canEdit));
  const filteredEvents = useMemo(() => searchEvents(events, query), [events, query]);
  const fetchEvents: EventSourceFunc = useCallback(
    async (info, success, failure) => {
      if (!selected.length) {
        setEvents([]);
        success([]);
        return;
      }
      try {
        const result = await api.events(info.start, info.end, selected);
        setEvents(result.events);
        setLoadError('');
        success(
          searchEvents(result.events, query).map((event) => ({
            id: event.occurrenceId,
            title: event.title,
            start: event.start,
            end: event.end,
            allDay: event.allDay,
            backgroundColor: '#f7f1e5',
            borderColor: event.calendarColor,
            textColor: '#183026',
            extendedProps: { occurrence: event },
          })),
        );
      } catch (cause) {
        const error = cause instanceof Error ? cause : new Error('Could not load events');
        setLoadError(error.message);
        failure(error);
      }
    },
    [query, selected],
  );

  const setUrl = (changes: Record<string, string | null>) => {
    const next = new URL(location.href);
    Object.entries(changes).forEach(([key, value]) =>
      value ? next.searchParams.set(key, value) : next.searchParams.delete(key),
    );
    history.replaceState({}, '', next);
  };
  const selectCalendars = (ids: string[]) => {
    setSelected(ids);
    setUrl({ calendars: ids.length === calendars.length ? null : ids.join(',') });
  };
  const changeView = (next: ViewName) => {
    setView(next);
    localStorage.setItem('dragonfly-calendar-view', next);
    setUrl({ view: next });
    calendarRef.current?.getApi().changeView(next);
  };
  const refresh = () => calendarRef.current?.getApi().refetchEvents();
  useEffect(() => {
    const refetch = () => refresh();
    const wentOnline = () => {
      setOnline(true);
      refetch();
    };
    const wentOffline = () => setOnline(false);
    const focusedWindow = () => document.visibilityState === 'visible' && refetch();
    window.addEventListener('online', wentOnline);
    window.addEventListener('offline', wentOffline);
    window.addEventListener('focus', refetch);
    document.addEventListener('visibilitychange', focusedWindow);
    return () => {
      window.removeEventListener('online', wentOnline);
      window.removeEventListener('offline', wentOffline);
      window.removeEventListener('focus', refetch);
      document.removeEventListener('visibilitychange', focusedWindow);
    };
  }, []);
  useEffect(() => {
    if (!staff) return;
    let stopped = false;
    let socket: WebSocket | undefined;
    let retry: number | undefined;
    let delay = 1_000;
    const connect = () => {
      if (
        stopped ||
        !navigator.onLine ||
        socket?.readyState === WebSocket.OPEN ||
        socket?.readyState === WebSocket.CONNECTING
      )
        return;
      const url = new URL('/live/staff', location.href);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(url);
      socket.addEventListener('open', () => {
        delay = 1_000;
        refresh();
      });
      socket.addEventListener('message', refresh);
      socket.addEventListener('close', () => {
        socket = undefined;
        if (stopped) return;
        retry = window.setTimeout(connect, delay);
        delay = Math.min(delay * 2, 30_000);
      });
    };
    const disconnect = () => socket?.close();
    connect();
    window.addEventListener('online', connect);
    window.addEventListener('offline', disconnect);
    return () => {
      stopped = true;
      if (retry) window.clearTimeout(retry);
      window.removeEventListener('online', connect);
      window.removeEventListener('offline', disconnect);
      socket?.close();
    };
  }, [staff]);
  const openEvent = (event: EventOccurrence) => {
    dismissPreview();
    rememberCalendarReturnTo(location.pathname + location.search);
    location.assign(eventDetailUrl(event.path, event.recurrenceId));
  };
  const editEvent = async (focused: EventOccurrence) => {
    try {
      const [{ event }, series] = await Promise.all([
        api.event(focused.id, focused.recurrenceId || undefined),
        focused.recurrenceId ? api.event(focused.id) : Promise.resolve(null),
      ]);
      setEditorEvent(event);
      setSeriesEvent(series?.event);
      setEditorRecurrenceId(focused.recurrenceId || undefined);
    } catch (cause) {
      setLoadError(cause instanceof Error ? cause.message : 'Could not open the event editor');
    }
  };
  const openNew = (date?: Date) => {
    setEditorStart(date);
    setSeriesEvent(undefined);
    setEditorRecurrenceId(undefined);
    setEditorEvent(null);
  };
  const saveGesture = async (
    gesture: NonNullable<typeof pendingGesture>,
    scope: 'occurrence' | 'series',
  ) => {
    setPendingGesture(undefined);
    setChangeNotice('Saving calendar change…');
    try {
      let input = gesture.input;
      let original = eventInput(gesture.occurrence);
      if (scope === 'series' && gesture.occurrence.recurrenceId) {
        const master = (await api.event(gesture.occurrence.id)).event;
        input = seriesMutationInput(master, gesture.occurrence, gesture.input);
        original = eventInput(master);
      }
      const result = await api.updateEvent(
        gesture.occurrence.id,
        input,
        gesture.occurrence.version,
        session?.csrfToken,
        gesture.occurrence.recurrenceId
          ? { scope, recurrenceId: gesture.occurrence.recurrenceId }
          : undefined,
      );
      setUndo({
        occurrence: gesture.occurrence,
        input: original,
        version: result.event.version,
        scope,
      });
      setChangeNotice('Calendar change saved.');
      refresh();
    } catch (cause) {
      gesture.revert();
      setUndo(undefined);
      setChangeNotice('');
      setLoadError(cause instanceof Error ? cause.message : 'Could not save the calendar change');
      refresh();
    }
  };
  const changeSchedule = (info: EventDropArg | EventResizeDoneArg) => {
    const occurrence = info.oldEvent.extendedProps.occurrence as EventOccurrence;
    if (!staff || !occurrence.canEdit || !info.event.startStr || !info.event.endStr) {
      info.revert();
      return;
    }
    const gesture = {
      occurrence,
      input: calendarMutationInput(
        occurrence,
        info.event.startStr,
        info.event.endStr,
        info.event.allDay,
      ),
      revert: info.revert,
    };
    if (occurrence.recurrenceId) setPendingGesture(gesture);
    else void saveGesture(gesture, 'series');
  };
  const undoChange = async () => {
    if (!undo) return;
    const pending = undo;
    setUndo(undefined);
    setChangeNotice('Undoing calendar change…');
    try {
      await api.updateEvent(
        pending.occurrence.id,
        pending.input,
        pending.version,
        session?.csrfToken,
        pending.occurrence.recurrenceId
          ? { scope: pending.scope, recurrenceId: pending.occurrence.recurrenceId }
          : undefined,
      );
      setChangeNotice('Calendar change undone.');
      refresh();
    } catch (cause) {
      setChangeNotice('');
      setLoadError(cause instanceof Error ? cause.message : 'Could not undo the calendar change');
      refresh();
    }
  };

  return (
    <main id="main-content" className="calendar-page">
      <section className="calendar-intro">
        <div>
          <span className="eyebrow">Together at school</span>
          <h1>Community calendar</h1>
          <p>{config.calendarTagline}</p>
        </div>
        {staff && (
          <button className="button new-event" onClick={() => openNew()}>
            <span aria-hidden="true">＋</span>New event
          </button>
        )}
      </section>
      <div className="calendar-toolbar">
        {!online && (
          <span className="offline-indicator" role="status">
            Offline. Page edits stay in the open editor. Reconnect before moving or resizing events.
          </span>
        )}
        <div className="date-controls">
          <button aria-label="Previous period" onClick={() => calendarRef.current?.getApi().prev()}>
            ←
          </button>
          <button onClick={() => calendarRef.current?.getApi().today()}>Today</button>
          <button aria-label="Next period" onClick={() => calendarRef.current?.getApi().next()}>
            →
          </button>
        </div>
        <div className="calendar-search">
          <label className="search-field">
            <span aria-hidden="true">⌕</span>
            <span className="sr-only">Search events</span>
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setUrl({ search: e.target.value || null });
              }}
              placeholder="Search events"
            />
            {query && (
              <button
                aria-label="Clear search"
                onClick={() => {
                  setQuery('');
                  setUrl({ search: null });
                }}
              >
                ×
              </button>
            )}
          </label>
          {query && (
            <div className="search-results" aria-live="polite">
              <span>
                {filteredEvents.length} matching {filteredEvents.length === 1 ? 'event' : 'events'}{' '}
                in this date range
              </span>
              {filteredEvents.slice(0, 5).map((event) => (
                <button key={event.occurrenceId} onClick={() => openEvent(event)}>
                  <i style={{ background: event.calendarColor }} />
                  <span>
                    <b>{event.title}</b>
                    <small>{formatEventRange(event)}</small>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          ref={filterRef}
          className="filter-toggle"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          Calendars <span>{selected.length}</span>
        </button>
        <div className="view-switcher" aria-label="Calendar view">
          {(
            [
              ['dayGridMonth', 'Month'],
              ['timeGridWeek', 'Week'],
              ['timeGridDay', 'Day'],
              ['listMonth', 'List'],
            ] as const
          ).map(([value, label]) => (
            <button key={value} aria-pressed={view === value} onClick={() => changeView(value)}>
              {label}
            </button>
          ))}
        </div>
        <button className="button subscribe-button" onClick={() => setSubscribe(true)}>
          Subscribe
        </button>
      </div>
      <div className="calendar-layout">
        <aside
          className={`calendar-sidebar ${filtersOpen ? 'is-open' : ''}`}
          aria-label="Calendar filters"
          onKeyDown={(event) => {
            if (event.key === 'Escape') closeFilters();
          }}
        >
          <div className="sidebar-heading">
            <h2>Calendars</h2>
            <button
              className="icon-button filters-close"
              aria-label="Close calendar filters"
              onClick={closeFilters}
            >
              ×
            </button>
            <button
              className="link-button"
              onClick={() =>
                selectCalendars(
                  selected.length === calendars.length
                    ? []
                    : calendars.map((calendar) => calendar.id),
                )
              }
            >
              {selected.length === calendars.length ? 'Clear' : 'Select all'}
            </button>
          </div>
          <ScrollableCalendarList>
            {calendars.map((calendar) => (
              <div className="filter-calendar" key={calendar.id}>
                <input
                  type="checkbox"
                  aria-label={`Include ${calendar.name} in combined calendar`}
                  checked={selected.includes(calendar.id)}
                  onChange={(e) =>
                    selectCalendars(
                      e.target.checked
                        ? [...selected, calendar.id]
                        : selected.filter((id) => id !== calendar.id),
                    )
                  }
                />
                <i style={{ background: calendar.color }} />
                <button
                  type="button"
                  className="calendar-isolate"
                  aria-label={`View only ${calendar.name}`}
                  onClick={() => selectCalendars([calendar.id])}
                >
                  <b>{calendar.name}</b>
                  <small>
                    {calendar.staffOnly
                      ? 'Staff only'
                      : calendar.visibility === 'private'
                        ? 'Members only'
                        : calendar.description}
                  </small>
                </button>
              </div>
            ))}
          </ScrollableCalendarList>
          <div className="sidebar-note">
            <span aria-hidden="true">✦</span>
            <p>
              <b>Make plans your way</b>
              <br />
              Subscribe to any combination and keep it synced in your calendar app.
            </p>
          </div>
        </aside>
        <section className="calendar-surface" aria-label="Events calendar">
          {loadError && (
            <div className="calendar-error" role="alert">
              {loadError} <button onClick={refresh}>Try again</button>
            </div>
          )}
          {changeNotice && (
            <div className="calendar-change-notice" role="status">
              <span>{changeNotice}</span>
              {undo && <button onClick={() => void undoChange()}>Undo</button>}
            </div>
          )}
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView={view}
            {...(params.get('date') ? { initialDate: params.get('date')! } : {})}
            headerToolbar={{ left: '', center: 'title', right: '' }}
            height="auto"
            nowIndicator
            selectable={staff}
            editable={staff && online}
            eventStartEditable={staff && online}
            eventDurationEditable={staff && online}
            dayMaxEvents={2}
            eventDisplay="block"
            displayEventTime={false}
            eventInteractive={false}
            eventContent={(info) => (
              <CalendarEventPreview
                event={info.event.extendedProps.occurrence as EventOccurrence}
                active={
                  calendarPreview?.event.occurrenceId ===
                  info.event.extendedProps.occurrence?.occurrenceId
                }
                previewId={previewId}
                onHover={previewOnHover}
                onFocus={previewOnFocus}
                onLeave={previewOnLeave}
                onBlur={previewOnBlur}
                onDismiss={dismissPreview}
                {...(staff && info.event.extendedProps.occurrence?.canEdit
                  ? { onEdit: (event: EventOccurrence) => void editEvent(event) }
                  : {})}
              />
            )}
            events={fetchEvents}
            datesSet={(info) =>
              setUrl({
                date: info.view.currentStart.toISOString().slice(0, 10),
                view: info.view.type,
              })
            }
            dateClick={(info) => staff && openNew(info.date)}
            select={(info) => staff && openNew(info.start)}
            eventAllow={(_dropInfo, draggedEvent) =>
              staff && online && Boolean(draggedEvent?.extendedProps.occurrence?.canEdit)
            }
            eventDrop={changeSchedule}
            eventResize={changeSchedule}
            eventClick={(info) => {
              if ((info.jsEvent.target as Element).closest('.calendar-event-edit')) return;
              openEvent(info.event.extendedProps.occurrence as EventOccurrence);
            }}
            eventClassNames={(info) =>
              info.event.extendedProps.occurrence?.status === 'cancelled'
                ? ['event-cancelled']
                : info.event.extendedProps.occurrence?.status === 'draft'
                  ? ['event-draft']
                  : []
            }
          />
        </section>
      </div>
      <CalendarEventTooltip
        preview={calendarPreview}
        previewId={previewId}
        onEnter={keepPreviewOpen}
        onLeave={closePreviewSoon}
      />
      {pendingGesture && (
        <div className="modal-backdrop">
          <section
            className="scope-dialog"
            ref={scopeRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="scope-title"
          >
            <h2 id="scope-title">Change repeating event</h2>
            <p>Apply this calendar change to one occurrence or the entire series?</p>
            <div className="summary-actions">
              <button
                className="button"
                onClick={() => void saveGesture(pendingGesture, 'occurrence')}
              >
                This occurrence
              </button>
              <button
                className="button button-quiet"
                onClick={() => void saveGesture(pendingGesture, 'series')}
              >
                Entire series
              </button>
              <button
                className="button button-quiet"
                onClick={() => {
                  pendingGesture.revert();
                  setPendingGesture(undefined);
                }}
              >
                Cancel
              </button>
            </div>
          </section>
        </div>
      )}
      {subscribe && (
        <SubscriptionModal
          calendars={calendars}
          session={session}
          onClose={() => setSubscribe(false)}
        />
      )}
      {editorEvent !== undefined && session && (
        <Suspense
          fallback={
            <div className="modal-backdrop">
              <div className="loading-card" role="status">
                Opening editor…
              </div>
            </div>
          }
        >
          <EventEditor
            calendars={calendars}
            session={session}
            event={editorEvent}
            {...(seriesEvent ? { seriesEvent } : {})}
            {...(editorRecurrenceId ? { recurrenceId: editorRecurrenceId } : {})}
            {...(editorStart ? { initialStart: editorStart } : {})}
            onClose={() => setEditorEvent(undefined)}
            onSaved={() => {
              setEditorEvent(undefined);
              refresh();
            }}
          />
        </Suspense>
      )}
    </main>
  );
}
